import {
  Button,
  Checkbox,
  LoadingOverlay,
  Menu,
  Select,
  Text,
} from "@mantine/core"
import { useDisclosure } from "@mantine/hooks"
import { motion } from "framer-motion"
import { FileSpreadsheet, Package, Plus, Search, Sliders } from "lucide-react"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { Link, useLocation, useParams } from "react-router-dom"
import {
  CollectionQuery,
  Filter,
  Order,
} from "../../../models/collection.model"
import {
  EntityConfig,
  entityViewMode,
} from "../../../models/entity-config.model"
import EntityList from "../../../shared/component/entity-list/entity-list.component"
import { useCurrentUser } from "../../../shared/hooks/current-user.hook"
import { dateFormat } from "../../../shared/utility/date-format/date-format"
import StatCard from "../../dashboard/component/stat-cart"
import { exportSales } from "../api/sale.api"
import ExportSalesModalComponent from "../component/export-sales-modal.component"

import { ParkingLot } from "../../../models/parking-lot.model"
import {
  useLazyCountParkingActivitiesQuery,
  useLazyGetParkingActivitiesQuery,
} from "../store/parking-activity.query"
import { ParkingActivity } from "../../../models/parking-activity.model"
import { IconFileExport, IconFileImport } from "@tabler/icons-react"
import { debounce } from "lodash"
import { use } from "i18next"
import { useLazyGetParkingLotsQuery } from "../../parking-lot/store/parking-lot.query"

export default function ParkingActivityPage() {
  const params = useParams()
  const location = useLocation()
  const { t } = useTranslation()
  const [viewMode, setViewMode] = useState<entityViewMode>("list")
  const [selectedLot, setSelectedLot] = useState<string | null>(null)
  const [openedExport, { open: openExport, close: closeExport }] =
    useDisclosure()
  const [openedImport, { open: openImport, close: closeImport }] =
    useDisclosure()
  const [fileOpened, { open: fileOpen, close: fileClose }] = useDisclosure()
  const [selectedFiles, setSelectedFiles] = useState<string[]>([])

  const [collection, setCollection] = useState<CollectionQuery>({
    skip: 0,
    top: 10,
    includes: ["parkingLot", "parkingLot.property"],
    orderBy: [{ field: "createdAt", direction: "desc" }],
  })

  const [lotCollection, setLotCollection] = useState<CollectionQuery>({
    skip: 0,
    top: 10,
    includes: ["property"],
  })

  const [getParkingActivities, parkingActivities] =
    useLazyGetParkingActivitiesQuery()
  const [countParkingActivities, totalParkingActivities] =
    useLazyCountParkingActivitiesQuery()
  const [countActiveParkingActivities, activeParkingActivities] =
    useLazyCountParkingActivitiesQuery()
  const [countInactiveParkingActivities, inactiveParkingActivities] =
    useLazyCountParkingActivitiesQuery()

  const [getParkingLots, parkingLots] = useLazyGetParkingLotsQuery()
  const [userInfo, userId, ownerInfo] = useCurrentUser()
  // const [removeSalesFile, removeSalesFileResponse] =
  //   useRemoveSalesFileMutation()
  const generateFilterOption = (value: boolean): Filter[][] => {
    if (userInfo.role.key == "super_admin") {
      // return [[{ field: "enabled", value: value as boolean, operator: "=" }]]
    }
    return [
      // [{ field: "enabled", value: value as boolean, operator: "=" }],
      // [{ field: "ownerId", value: ownerInfo?.parentOwnerId, operator: "=" }],
    ]
  }

  useEffect(() => {
    if (userInfo?.role?.key == "super_admin") {
      getParkingLots(lotCollection)
    } else {
      getParkingLots({
        ...lotCollection,
        filter: [
          [
            {
              field: "property.ownerId",
              value: ownerInfo?.parentOwnerId,
              operator: "=",
            },
          ],
        ],
      })
    }
  }, [getParkingLots, lotCollection])

  useEffect(() => {
    if (selectedLot) {
      setCollection({
        ...collection,
        filter: [
          [{ field: "parkingLotId", value: selectedLot, operator: "=" }],
        ],
      })
    } else {
      setCollection({
        ...collection,
      })
    }
  }, [selectedLot])
  useEffect(() => {
    if (userInfo?.role?.key == "super_admin") {
      getParkingActivities({
        ...collection,
        // filter: [
        //   [{ field: "owner.confidential", value: false, operator: "=" }],
        // ],
      })
    } else {
      getParkingActivities({
        ...collection,
        filter: [
          [
            {
              field: "property.ownerId",
              value: ownerInfo?.parentOwnerId,
              operator: "=",
            },
          ],
        ],
      })
    }
  }, [collection])
  useEffect(() => {
    if (userInfo.role.key == "super_admin") {
      countParkingActivities({
        count: true,
      })
    } else {
      countParkingActivities({
        count: true,
        includes: ["parkingLot", "parkingLot.property"],
        filter: [
          [
            {
              field: "property.ownerId",
              value: ownerInfo?.parentOwnerId,
              operator: "=",
            },
          ],
        ],
      })
    }
    countActiveParkingActivities({
      count: true,
      filter: generateFilterOption(true),
    })
    countInactiveParkingActivities({
      count: true,
      filter: generateFilterOption(false),
    })
  }, [])

  useEffect(() => {
    if (params?.id !== undefined) {
      setViewMode("detail")
    } else {
      setViewMode("list")
    }
    if (location.pathname === "/tenant/archived") {
      setViewMode("detail")
    }
  }, [setViewMode, params?.id, location])

  const data: ParkingActivity[] | undefined = parkingActivities.data?.data

  const config: EntityConfig<ParkingActivity> = {
    primaryColumn: {
      key: "name",
      name: t("name"),
      render: (parkingActivity: ParkingActivity) => {
        return (
          <Text
            size="sm"
            className={parkingActivity?.deletedAt ? "text-danger" : ""}
          >
            {parkingActivity?.parkingLot?.name}
          </Text>
        )
      },
    },
    detailUrl: "detail",
    rootUrl: "/parking-activity",
    identity: "id",
    name: "name",
    showDetail: true,
    visibleColumn: [
      {
        key: "property",
        name: t("property", { count: 1 }),
        render(value) {
          return <>{value?.parkingLot?.property?.name}</>
        },
      },
      {
        key: "name",
        name: t("parkingLot", { count: 1 }),
        render: (parkingActivity: ParkingActivity) => {
          return (
            <Text
              size="sm"
              className={parkingActivity?.deletedAt ? "text-danger" : ""}
            >
              {parkingActivity?.parkingLot?.name}
            </Text>
          )
        },
      },
      { key: "licensePlate", name: t("plateNumber") },
      { key: "phoneNumber", name: t("phoneNumber") },
      { key: "entryTime", name: t("entryTime") },
      { key: "exitTime", name: t("exitTime") },
      {
        key: "price",
        name: t("price"),
        render(value) {
          return <Text size="sm">{value?.price?.toFixed(2)} ETB</Text>
        },
      },
      {
        key: "createdAt",
        name: t("createdAt"),
        isDate: true,
        render: (value: ParkingActivity) => {
          return <Text size="sm">{dateFormat(value?.createdAt)}</Text>
        },
      },
      {
        key: "status",
        name: t("status"),
        isBoolean: true,
        // render: (value: ParkingLot) => {
        //   return <Text size="sm">{dateFormat(value)}</Text>
        // },
      },
    ],
  }

  function _parseLots() {
    let data: { label: string; value: string }[] = []

    parkingLots?.data?.data?.map((parkingLot: ParkingLot) => {
      data.push({
        label: `${parkingLot?.name as string} (${
          parkingLot?.property?.name as string
        })`,
        value: parkingLot?.id as string,
      })
    })
    return data
  }

  return (
    <main className="max-w-7xl mx-auto py-6 px-4 lg:px-8 mt-16">
      <LoadingOverlay visible={parkingActivities.isFetching} />

      <motion.div
        className="hidden md:grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 mb-8"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1 }}
      >
        <StatCard
          name={t("totalParkingActivities")}
          icon={Package}
          value={totalParkingActivities?.data?.count}
          color="#6366F1"
        />
        {/* <StatCard
          name={t("activeTenants")}
          icon={TrendingUp}
          value={activeSales?.data?.count}
          color="#8B5CF6"
        />
        <StatCard
          name={t("inactiveTenants")}
          icon={AlertTriangle}
          value={inactiveSales?.data?.count}
          color="#EC4899"
        /> */}
        {/* <StatCard name='Total Revenue' icon={DollarSign} value='12.5%' color='#10B981' /> */}
      </motion.div>
      <EntityList
        viewMode={viewMode}
        title={t("parkingActivity", { count: 2 })}
        showExport={false}
        header={
          <>
            <div className="flex  justify-between items-center mb-6">
              <h2 className="text-xl font-semibold text-gray-100">
                {t("parkingActivity", { count: 1 })}
              </h2>

              <div className="gap-2 hidden md:flex">
                {/* {showExport && (
                  <div
                    onClick={onExport}
                    className="flex items-center justify-center gap-2 cursor-pointer"
                    title="Download Excel"
                  >
                    <FileSpreadsheet />
                  </div>
                )} */}
                <Select
                  className="bg-gray-700 text-white placeholder-gray-400 rounded-lg  focus:outline-none focus:ring-2 focus:ring-blue-500"
                  data={_parseLots()}
                  clearable
                  placeholder={t("parkingLot", { count: 1 })}
                  value={selectedLot}
                  onChange={(value: string) => setSelectedLot(value)}
                />
                <div className="relative">
                  <input
                    type="text"
                    placeholder={t("searchHere")}
                    className="bg-gray-700 text-white placeholder-gray-400 rounded-lg pl-10 pr-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    onKeyUp={debounce(
                      (event: any) =>
                        setCollection({
                          ...collection,
                          search: event.target.value,
                          searchFrom: ["name"],
                        }),
                      1000,
                    )}
                  />
                  <Search
                    className="absolute left-3 top-2.5 text-gray-400"
                    size={18}
                  />
                </div>

                <div className="flex gap-2">
                  {/* {setting?.filter && (
                    <Menu
                      withArrow
                      position="left-start"
                      closeOnItemClick={false}
                      classNames={{ itemLabel: "gap-2" }}
                    >
                      <Menu.Target>
                        <Button
                          className={` dark:bg-dark_primary dark:border-none dark:text-white`}
                          classNames={{
                            label: "flex space-x-2  text-gray-700",
                          }}
                          size="xs"
                          variant="default"
                        >
                          <span>
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="fill-current h-4 dark:text-white"
                              viewBox="0 0 26 26"
                            >
                              <path d="M2 0C0.894531 0 0 0.894531 0 2L9 12L9 21.40625C9 22.511719 9.894531 23.777344 11 24.21875L15 25.8125C15.261719 25.917969 15.519531 25.96875 15.75 25.96875C16.488281 25.96875 17 25.464844 17 24.625L17 12L26 2C26 0.894531 25.105469 0 24 0 Z M 2.6875 2L23.3125 2L16.125 10L9.90625 10 Z M 11 12L15 12L15 23.65625L11.75 22.34375C11.367188 22.191406 11 21.648438 11 21.40625Z" />
                            </svg>
                          </span>
                          <span
                            className={`${
                              viewMode === "detail" && "hidden"
                            } dark:text-white`}
                          >
                            {t("filter")}
                          </span>
                        </Button>
                      </Menu.Target>
                      <Menu.Dropdown>
                        <Menu.Label>{t("filterBy")}</Menu.Label>
                        <Menu.Item>
                          <Checkbox.Group
                            size="xs"
                            withAsterisk
                            defaultValue={filterValue}
                            value={filterValue}
                            onChange={(data: any) => {
                              setFilterValue(data)
                            }}
                          >
                            {setting?.filter?.map((item, idx) => (
                              <>
                                <div className="flex-col space-y-2">
                                  {item.map((filter, index) => (
                                    <Checkbox
                                      key={index}
                                      value={JSON.stringify(filter)}
                                      label={filter.name}
                                    />
                                  ))}
                                  {setting !== undefined ? (
                                    setting?.filter?.length !== idx + 1 && (
                                      <Divider
                                        label={t("and")}
                                        labelPosition="center"
                                        my={"sm"}
                                      />
                                    )
                                  ) : (
                                    <></>
                                  )}
                                </div>
                              </>
                            ))}
                          </Checkbox.Group>
                        </Menu.Item>
                      </Menu.Dropdown>
                    </Menu>
                  )} */}
                  {/* {showSelector && (
                    <Tooltip label={t("selector")}>
                      <Checkbox
                        size="lg"
                        className=" rounded-l"
                        icon={CheckboxIcon}
                        onClick={() => setCheck(!check)}
                        indeterminate
                      />
                    </Tooltip>
                  )} */}

                  <div className="flex justify-center items-center">
                    <Checkbox
                      size="sm"
                      onChange={(e) =>
                        setCollection({
                          ...collection,
                          withArchived: e.currentTarget.checked ? true : false,
                        })
                      }
                      label={
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {t("showArchived")}
                        </span>
                      }
                    />
                  </div>

                  <Link
                    to={`/parking-activity/new`}
                    state={{ id: location?.state?.id }}
                  >
                    <Button
                      classNames={{ label: "flex space-x-2 text-white" }}
                      className="bg-primary dark:bg-dark_primary"
                      size="xs"
                      variant="filled"
                    >
                      <span className="text-white">
                        <Plus />
                      </span>
                      <span>{t("new")}</span>
                    </Button>
                  </Link>
                  <Link
                    to={`/parking-activity/portal/${ownerInfo?.parentOwnerId}`}
                    state={{ id: location?.state?.id }}
                  >
                    <Button
                      classNames={{ label: "flex space-x-2 text-white" }}
                      className="bg-primary dark:bg-dark_primary"
                      size="xs"
                      variant="filled"
                    >
                      {/* <span className="text-white">
                        <Plus />
                      </span> */}
                      <span>Activity</span>
                    </Button>
                  </Link>
                </div>
              </div>

              <div className="flex justify-end gap-2 md:hidden">
                {/* <div className="relative"> */}
                <input
                  type="text"
                  placeholder={t("searchHere")}
                  className="bg-gray-700 w-[50%] h-7 text-white placeholder-gray-400 rounded-lg pl-2 md:pl-10 pr-1 md:pr-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  onKeyUp={debounce(
                    (event: any) =>
                      setCollection({
                        ...collection,
                        search: event.target.value,
                        searchFrom: ["name"],
                      }),
                    1000,
                  )}
                />
                {/* <Search
                  className="absolute left-3 top-2.5 text-gray-400"
                  size={18}
                />
              </div> */}

                <Menu
                  trigger="click"
                  openDelay={100}
                  closeDelay={400}
                  closeOnItemClick={false}
                >
                  <Menu.Target>
                    <Sliders />
                  </Menu.Target>

                  <Menu.Dropdown>
                    {/* <Menu.Label>
                    {showSelector && (
                      <Tooltip label="Selector">
                        <Checkbox
                          size="lg"
                          className=" rounded-l"
                          icon={CheckboxIcon}
                          onClick={() => setCheck(!check)}
                          indeterminate
                        />
                      </Tooltip>
                    )}
                  </Menu.Label> */}
                    <Menu.Item>
                      <div className="flex justify-center items-center">
                        <Checkbox
                          size="sm"
                          onChange={(e) =>
                            setCollection({
                              ...collection,
                              withArchived: e.currentTarget.checked
                                ? true
                                : false,
                            })
                          }
                          label={
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              {t("showArchived")}
                            </span>
                          }
                        />
                      </div>
                    </Menu.Item>
                    <Menu.Item>
                      <Link
                        to={`/parking-activity/new`}
                        state={{ id: location?.state?.id }}
                      >
                        <Button
                          classNames={{
                            label: "flex space-x-2 text-white",
                          }}
                          className="bg-primary dark:bg-dark_primary w-full"
                          size="xs"
                          variant="filled"
                        >
                          <span className="text-white">
                            <Plus />
                          </span>
                          <span>{t("new")}</span>
                        </Button>
                      </Link>
                    </Menu.Item>
                    <Menu.Item>
                      <Select
                        className="bg-gray-700 text-white placeholder-gray-400 rounded-lg  focus:outline-none focus:ring-2 focus:ring-blue-500"
                        data={_parseLots()}
                        clearable
                        placeholder={"Select Parking Lot"}
                        value={selectedLot}
                        onChange={(value: string) => setSelectedLot(value)}
                      />
                    </Menu.Item>
                  </Menu.Dropdown>
                </Menu>
              </div>
            </div>
          </>
        }
        total={parkingActivities?.data?.count}
        itemsLoading={parkingActivities.isFetching}
        config={config}
        items={data}
        initialPage={1}
        defaultPageSize={10}
        pageSize={[10, 15, 20]}
        collectionQuery={collection}
        onArchivedChecked={(e) =>
          setCollection({
            ...collection,
            withArchived: e,
          })
        }
        onPaginationChange={(skip: number, top: number) => {
          if (collection.skip !== skip || collection.top !== top) {
            setCollection({ ...collection, skip: skip, top: top })
          }
        }}
        onSearch={(data: any) => {
          setCollection({
            ...collection,
            search: data,
            searchFrom: ["name"],
          })
        }}
        onFilterChange={(data: any) => {
          if (collection?.filter || data.length > 0) {
            setCollection({ ...collection, filter: data })
          }
        }}
        onOrder={(data: Order) =>
          setCollection({ ...collection, orderBy: [data] })
        }
        onExport={openExport}
      />

      <ExportSalesModalComponent
        open={openExport}
        opened={openedExport}
        close={closeExport}
        filename={"sales.xlsx"}
        // filteringVariables={[
        //   [
        //     {
        //       field: "enabled",
        //       value: true,
        //       name: t("active"),
        //       operator: "=",
        //     },
        //     {
        //       field: "enabled",
        //       value: false,
        //       name: t("inactive"),
        //       operator: "=",
        //     },
        //   ],
        //   [
        //     {
        //       field: "gender",
        //       value: "male",
        //       name: t("male"),
        //       operator: "=",
        //     },
        //     {
        //       field: "gender",
        //       value: "female",
        //       name: t("female"),
        //       operator: "=",
        //     },
        //   ],
        // ]}
        exportFunction={exportSales}
      />

      {/* <FileViewerModal
        open={fileOpen}
        close={fileClose}
        fileType={t("sale", { count: 1 })}
        files={selectedFiles as string[]}
        opened={fileOpened}
        remove={handleFileRemoval}
        removeLoading={removeSalesFileResponse?.isLoading}
        fileNames={[]}
      /> */}
    </main>
  )
}
